import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';

export function useIntegrationProviders() {
  return useQuery({
    queryKey: ['admin', 'integration-providers'],
    queryFn: () => admin.listIntegrationProviders(),
    staleTime: 60_000
  });
}

export function useIntegrations() {
  return useQuery({
    queryKey: ['admin', 'integrations'],
    queryFn: () => admin.listIntegrations()
  });
}

export function useIntegration(providerCode: string | null, reveal = false) {
  return useQuery({
    enabled: !!providerCode,
    queryKey: ['admin', 'integration', providerCode, { reveal }],
    queryFn: () => admin.getIntegration(providerCode!, reveal ? { reveal: true } : undefined),
    /*
     * A revealed row carries every secret for that provider in plaintext. It
     * used to sit in the query cache for the default five minutes after the
     * drawer closed, so the credentials outlived the screen that asked for
     * them. Revealed rows are dropped the moment nothing is rendering them;
     * masked rows keep the normal caching.
     */
    gcTime: reveal ? 0 : undefined,
    staleTime: reveal ? 0 : undefined
  });
}

export function useUpsertIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ providerCode, ...req }: { providerCode: string } & admin.UpsertIntegrationRequest) =>
      admin.upsertIntegration(providerCode, req),
    onSuccess: (_data, { providerCode }) => {
      qc.invalidateQueries({ queryKey: ['admin', 'integrations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'integration', providerCode] });
    }
  });
}

export function useTestIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (providerCode: string) => admin.testIntegration(providerCode),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'integrations'] })
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (providerCode: string) => admin.deleteIntegration(providerCode),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'integrations'] })
  });
}
